import { getMobileOperatingSystem, getSma, Platform, Sma } from 'src/utils/user';
import { AuthStatus, AUTH_REASON_PARAM, AUTH_SOURCE_PARAM } from 'src/utils/auth';
const createMessage = (code, details, url) => {
    const message = { messageCode: code };
    if (details)
        message.messageDetails = details;
    if (url)
        message.url = url;
    return JSON.stringify(message);
};
const sendMessageToSma = (jsonMessage) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const platform = getMobileOperatingSystem();
    if (platform === Platform.IOS) {
        (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.AmazonShopExternalInterfaceHandler) === null || _c === void 0 ? void 0 : _c.postMessage(jsonMessage);
    }
    else if (platform === Platform.ANDROID) {
        (_d = window.AmazonShopExternalInterfaceHandler) === null || _d === void 0 ? void 0 : _d.postMessage(jsonMessage);
    }
    else {
        // Fallback: try both
        (_e = window.AmazonShopExternalInterfaceHandler) === null || _e === void 0 ? void 0 : _e.postMessage(jsonMessage);
        (_h = (_g = (_f = window.webkit) === null || _f === void 0 ? void 0 : _f.messageHandlers) === null || _g === void 0 ? void 0 : _g.AmazonShopExternalInterfaceHandler) === null || _h === void 0 ? void 0 : _h.postMessage(jsonMessage);
    }
};
// Track off ramp messages to detect duplicates.
// If SMAs fail to react to the off-ramp message, it'll likely result in the user clicking
// the same link again. We want to track how often this happens and alarm on it to identify
// when the SMA integration is not working properly.
const sentUrls = new Map();
export const sendOpenLinkMessage = (rumClient, href) => {
    const count = sentUrls.get(href) || 0;
    const newCount = count + 1;
    sentUrls.set(href, newCount);
    if (newCount > 1) {
        rumClient.recordEvent('JSMESSAGE.DUPLICATE_OPEN_EXTERNAL_LINK', {
            url: href,
            count: newCount,
        });
    }
    rumClient.recordEvent('JSMESSAGE.OPEN_EXTERNAL_LINK', { url: href });
    // TikTok and Snapchat on iOS don't handle the OPEN_EXTERNAL_LINK message correctly, so use window.open instead
    const sma = getSma();
    const platform = getMobileOperatingSystem();
    if ((sma === Sma.TIKTOK || sma === Sma.SNAPCHAT) && platform === Platform.IOS) {
        window.open(href, '_self');
    }
    else {
        sendMessageToSma(createMessage('OPEN_EXTERNAL_LINK', 'User clicked on a link.', href));
    }
};
// Track close view messages to detect duplicates
// If SMAs fail to react to the close message, users may click close multiple times.
// We track this to identify integration issues.
let closeViewCount = 0;
export const sendCloseMessage = (rumClient) => {
    closeViewCount++;
    if (closeViewCount > 1) {
        rumClient.recordEvent('JSMESSAGE.DUPLICATE_CLOSE_VIEW', {
            count: closeViewCount,
        });
    }
    rumClient.recordEvent('JSMESSAGE.CLOSE_VIEW', {});
    sendMessageToSma(createMessage('CLOSE_VIEW', 'User closed the view.'));
};
// Track auth messages to detect duplicate auth flows
// If SMAs fail to handle auth messages properly, users may trigger the same auth flow multiple times.
// We track this to identify integration issues.
// This also counts users declining the auth flow before clicking on the continue button again.
const sentAuthMessages = new Map();
export const sendAuthMessage = (rumClient, authStatus, redirectUrl, source) => {
    const finalRedirectUrl = redirectUrl || window.location.href;
    // Handle relative URLs by using window.location as base
    const url = finalRedirectUrl.startsWith('http')
        ? new URL(finalRedirectUrl)
        : new URL(finalRedirectUrl, window.location.origin);
    // Add query parameters using URLSearchParams
    url.searchParams.set(AUTH_REASON_PARAM, authStatus);
    if (source) {
        url.searchParams.set(AUTH_SOURCE_PARAM, source);
    }
    const finalUrl = url.toString();
    // Track duplicate auth messages by status only
    const count = sentAuthMessages.get(authStatus) || 0;
    const newCount = count + 1;
    sentAuthMessages.set(authStatus, newCount);
    if (newCount > 1) {
        rumClient.recordEvent(`JSMESSAGE.DUPLICATE_${authStatus}`, {
            url: finalUrl,
            count: newCount,
        });
    }
    if (authStatus === AuthStatus.AUTHENTICATE) {
        rumClient.recordEvent('JSMESSAGE.AUTHENTICATE', { url: finalUrl });
        sendMessageToSma(createMessage('AUTHENTICATE', 'The auth token is invalid.', finalUrl));
    }
    else if (authStatus === AuthStatus.REFRESH_TOKEN) {
        rumClient.recordEvent('JSMESSAGE.REFRESH_TOKEN', { url: finalUrl });
        sendMessageToSma(createMessage('REFRESH_TOKEN', 'The auth token is expired.', finalUrl));
    }
};
